import { isRosterAssetKey, ROSTER_ASSET_KEYS, type RosterAssetKey } from "./assets";
import { talkBrand } from "./brand";

const callsignModifiers = [
  "Quiet", "Cobalt", "Silent", "Steady", "Swift", "Keen", "Signal", "Packet",
  "Cipher", "Midnight", "Copper", "Azure", "Hidden", "Calm", "Bright", "Velvet",
] as const;

const callsignNouns = [
  "Raven", "Fox", "Relay", "Beacon", "Nomad", "Lynx", "Falcon", "Vector",
  "Sparrow", "Comet", "Circuit", "Harbor", "Echo", "Lantern", "Kite", "Badger",
] as const;

export interface GuestIdentityDraft {
  displayName: string;
  assetKey: RosterAssetKey;
}

function storageKey(roomId: string): string {
  return `${talkBrand.guestStoragePrefix}:guest-identity:${roomId}`;
}

function secureIndex(length: number): number {
  if (length < 2) return 0;
  const values = new Uint32Array(1);
  crypto.getRandomValues(values);
  return values[0]! % length;
}

const chessCallsigns = [
  "Белая пешка", "Белый конь", "Белый слон", "Белая ладья", "Белая ферзь", "Белый король",
  "Чёрная пешка", "Чёрный конь", "Чёрный слон", "Чёрная ладья", "Чёрная ферзь", "Чёрный король",
] as const;

export function randomGuestCallsign(index = secureIndex): string {
  if (talkBrand.id === "chess") return chessCallsigns[index(chessCallsigns.length)]!;
  return `${callsignModifiers[index(callsignModifiers.length)]} ${callsignNouns[index(callsignNouns.length)]}`;
}

export function createGuestIdentityDraft(index = secureIndex): GuestIdentityDraft {
  return {
    displayName: randomGuestCallsign(index),
    assetKey: ROSTER_ASSET_KEYS[index(ROSTER_ASSET_KEYS.length)]!,
  };
}

export function shuffleGuestCallsign(draft: GuestIdentityDraft, index = secureIndex): GuestIdentityDraft {
  let displayName = randomGuestCallsign(index);
  if (displayName === draft.displayName) {
    if (talkBrand.id === "chess") {
      const current = chessCallsigns.indexOf(draft.displayName as (typeof chessCallsigns)[number]);
      displayName = chessCallsigns[(Math.max(current, 0) + 1) % chessCallsigns.length]!;
    } else {
      const fallback = `${callsignModifiers[0]} ${callsignNouns[0]}`;
      displayName = draft.displayName === fallback ? `${callsignModifiers[1]} ${callsignNouns[0]}` : fallback;
    }
  }
  return { ...draft, displayName };
}

function validDraft(value: unknown): value is GuestIdentityDraft {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<GuestIdentityDraft>;
  return typeof candidate.displayName === "string"
    && candidate.displayName.trim().length > 0
    && candidate.displayName.length <= 64
    && isRosterAssetKey(candidate.assetKey);
}

export function saveGuestIdentityDraft(roomId: string, draft: GuestIdentityDraft): void {
  if (!roomId || !validDraft(draft)) return;
  try {
    window.sessionStorage.setItem(storageKey(roomId), JSON.stringify(draft));
  } catch {
    // Storage is a convenience only; joining remains available without it.
  }
}

export function loadGuestIdentityDraft(roomId: string): GuestIdentityDraft | null {
  if (!roomId) return null;
  try {
    const stored = window.sessionStorage.getItem(storageKey(roomId));
    if (!stored) return null;
    const parsed: unknown = JSON.parse(stored);
    return validDraft(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function loadOrCreateGuestIdentityDraft(roomId: string): GuestIdentityDraft {
  const existing = loadGuestIdentityDraft(roomId);
  if (existing) return existing;
  const draft = createGuestIdentityDraft();
  saveGuestIdentityDraft(roomId, draft);
  return draft;
}

export function clearGuestIdentityDraft(roomId: string): void {
  if (!roomId) return;
  try {
    window.sessionStorage.removeItem(storageKey(roomId));
  } catch {
    // Nothing else depends on storage cleanup.
  }
}
